// Phase 87ba — Parsing robuste des ISO strings.
// Nayax envoie `AuthorizationDateTimeGMT` au format "2026-06-27T01:00:00.817"
// (sans Z) qui sont des heures UTC implicites. Sans Z, JS les parse comme
// heures LOCALES → écart de 1-2 h selon la saison (CET/CEST). Notre back-end
// stocke aussi sans Z par défaut (héritage Nayax). Pour ne plus se faire piéger,
// on assume "UTC si pas de tz suffix" et on affiche TOUJOURS en Europe/Zurich.
export const parseIso = (iso) => {
  if (!iso) return null;
  const s = String(iso);
  // Format date-only ("2026-06-27") → laisser tel quel (JS le traite en UTC)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(s + "T00:00:00Z");
    return isNaN(d.getTime()) ? null : d;
  }
  // ISO avec tz suffix (Z ou ±HH:MM) → JS parse correctement
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
  const safe = hasTz ? s : s + "Z"; // si pas de tz, forcer UTC
  const d = new Date(safe);
  return isNaN(d.getTime()) ? null : d;
};

const ZURICH_TZ = "Europe/Zurich";

export const fmtDate = (iso) => {
  const d = parseIso(iso);
  if (!d) return "—";
  return d.toLocaleDateString("fr-CH", {
    timeZone: ZURICH_TZ,
    day: "2-digit", month: "short", year: "numeric",
  });
};

export const fmtDateTime = (iso) => {
  const d = parseIso(iso);
  if (!d) return "—";
  return d.toLocaleString("fr-CH", {
    timeZone: ZURICH_TZ,
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// Renvoie une Date positionnée à 00:00:00 heure suisse pour la date d'aujourd'hui.
// Utile pour comparer "aujourd'hui en Suisse" sans dépendre de la TZ navigateur.
export const todayStartZurich = () => {
  // Astuce : on calcule l'instant correspondant à 00:00 Zurich aujourd'hui.
  // Intl ne donne pas de Date construit, donc on récupère les composants
  // de "maintenant" dans Zurich, puis on construit une Date UTC.
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZURICH_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (k) => parts.find((p) => p.type === k)?.value || "00";
  // 00:00 Zurich aujourd'hui (en heure locale du browser, qui est OK pour
  // comparaison avec d'autres Date Zurich)
  const isoZurich = `${get("year")}-${get("month")}-${get("day")}T00:00:00`;
  // On parse via parseIso (Z forcé) puis on rectifie de l'offset Zurich
  // pour rester précis. Simpler approach: utiliser un timestamp directement.
  // Méthode propre : itérer jusqu'à matcher l'heure Zurich attendue.
  // En pratique, comparer en Date timestamp suffit ici car on compare uniquement
  // si une autre Date est >= cette date "aujourd'hui Zurich".
  // On utilise donc l'instant qui correspond à 00:00 Zurich.
  // Diff actuel entre now et "maintenant Zurich rendu" donne l'offset.
  const nowZurichStr = `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`;
  const nowZurichAsLocal = new Date(nowZurichStr); // interprété en heure du navigateur
  const offsetMs = now.getTime() - nowZurichAsLocal.getTime();
  const midnightZurichAsLocal = new Date(isoZurich); // interprété en heure du navigateur
  return new Date(midnightZurichAsLocal.getTime() + offsetMs);
};

export const fmtNum = (n, unit = "") => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  const s = v % 1 === 0 ? v.toString() : v.toFixed(2);
  return unit ? `${s} ${unit}` : s;
};

export const statusLabel = {
  PLANIFIE: "Planifié",
  EN_COURS: "En cours",
  TERMINE: "Terminé",
  ANNULE: "Annulé",
};

export const movementLabel = {
  IN: "Entrée",
  OUT: "Sortie",
  ADJUST: "Ajustement",
};

// Statut DLC : "expired" (dépassée), "soon" (≤ 7j), "ok", ou null si pas de DLC
export const dlcStatus = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return { kind: "expired", days: diff };
  if (diff <= 7) return { kind: "soon", days: diff };
  return { kind: "ok", days: diff };
};

export const dlcBadgeClass = (kind) => {
  if (kind === "expired") return "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]";
  if (kind === "soon") return "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]";
  return "bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0]";
};

// ============================================================================
// Phase 101bq — Helpers pour <input type="datetime-local">
// ============================================================================
// Un input `datetime-local` travaille en HEURE LOCALE (celle du navigateur).
// Or nos ISO string en DB sont en UTC (avec ou sans Z). Sans conversion, on
// obtient un décalage de 1-2 h (CET/CEST) entre le picker et le PDF (qui, lui,
// convertit correctement via toLocaleString + timeZone Zurich).
//
// - `isoToLocalInput(iso)` : ISO UTC → "YYYY-MM-DDTHH:mm" en heure Zurich.
// - `localInputToIso(local)` : "YYYY-MM-DDTHH:mm" (Zurich) → ISO UTC.
//
// Bornes de sécurité :
// - Si tous les navigateurs des utilisateurs sont en TZ Zurich (99 % des cas),
//   on peut se baser sur la TZ browser. Ces helpers utilisent toutefois
//   `Europe/Zurich` explicitement pour résister à un navigateur voyageur.
// ============================================================================

const _padTwo = (n) => String(n).padStart(2, "0");

export const isoToLocalInput = (iso) => {
  const d = parseIso(iso);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZURICH_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const g = (k) => parts.find((p) => p.type === k)?.value || "";
  const hh = g("hour") === "24" ? "00" : g("hour"); // Intl edge case
  return `${g("year")}-${g("month")}-${g("day")}T${hh}:${g("minute")}`;
};

export const localInputToIso = (local) => {
  if (!local) return null;
  const [datePart, timePart] = String(local).split("T");
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;

  // On veut trouver l'instant UTC qui, affiché en TZ Zurich, correspond
  // exactement à YYYY-MM-DD HH:mm saisi. Approche : construire une "date UTC
  // naïve" avec ces mêmes composants, calculer ce que Zurich affiche pour cet
  // instant, en déduire l'offset, corriger.
  const naiveUtcMs = Date.UTC(y, m - 1, d, hh, mm, 0);
  const zurichParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZURICH_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(naiveUtcMs));
  const zg = (k) => zurichParts.find((p) => p.type === k)?.value || "0";
  const zurichNaiveMs = Date.UTC(
    Number(zg("year")), Number(zg("month")) - 1, Number(zg("day")),
    Number(zg("hour") === "24" ? "0" : zg("hour")), Number(zg("minute")), 0
  );
  const offsetMs = zurichNaiveMs - naiveUtcMs; // Zurich est en avance sur UTC
  const correctedUtcMs = naiveUtcMs - offsetMs;
  return new Date(correctedUtcMs).toISOString();
};

// Variante "date only" (type="date") — retourne "YYYY-MM-DD" en Zurich.
export const isoToLocalDate = (iso) => {
  const d = parseIso(iso);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZURICH_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const g = (k) => parts.find((p) => p.type === k)?.value || "";
  return `${g("year")}-${g("month")}-${g("day")}`;
};
